import React from "react";
import { Box, Card, CardBody, Flex, HStack, Tag, Text } from "@chakra-ui/react";
import { Barcode, Buildings, CalendarBlank, CurrencyDollar, Leaf, User } from "@phosphor-icons/react";
import { COMPANY_WIDE, TASK_DATA_TYPE_CONFIG, TASK_STATUSES } from "../../suppliersMock";

// Resolve the Phosphor icon named in TASK_DATA_TYPE_CONFIG.
const DATA_TYPE_ICONS = { Barcode, CurrencyDollar, Leaf };

const STATUS_CONFIG = {
  [TASK_STATUSES.NOT_STARTED]: { progress: 12, color: "#FC8181", label: "Not started" },
  [TASK_STATUSES.IN_PROGRESS]: { progress: 50, color: "#FBD38D", label: "In progress" },
  [TASK_STATUSES.COMPLETE]:    { progress: 100, color: "#B8E086", label: "Complete" },
};

// Supplier data-collection task card. Header carries a single data-type chip
// (Product / Revenue / Decarbonisation) rather than GHG scope pills.
function SupplierTaskCard({ task, onOpen }) {
  const statusCfg = STATUS_CONFIG[task.status] || STATUS_CONFIG[TASK_STATUSES.NOT_STARTED];
  const typeCfg = TASK_DATA_TYPE_CONFIG[task.dataType] || { light: "gray.100", text: "gray.600" };
  const TypeIcon = DATA_TYPE_ICONS[typeCfg.icon];
  const period = [task.year, task.quarter !== "ALL" ? task.quarter : null].filter(Boolean).join(" · ");
  // When the card lists tasks across suppliers, lead with the supplier name.
  const title = task.supplierName || period;

  return (
    <Card w="100%" borderWidth="1px" borderRadius="lg" borderColor="gray.200" bg="white" boxShadow="sm" cursor="pointer" _hover={{ borderColor: "lime" }} transition="border-color 0.15s" onClick={onOpen}>
      <Flex align="center" bg="gray.50" px={5} py={3} borderBottom="1px solid" borderColor="gray.100" borderTopRadius="lg">
        <Box
          display="inline-flex"
          alignItems="center"
          gap="4px"
          bg={typeCfg.light}
          color={typeCfg.text}
          fontSize="11px"
          fontWeight="600"
          px="10px"
          h="22px"
          borderRadius="full"
          whiteSpace="nowrap"
        >
          {TypeIcon && <TypeIcon size={12} weight="fill" />}
          {task.dataType}
        </Box>
      </Flex>

      <CardBody px={5} py={4}>
        <Text fontSize="15px" fontWeight="600" color="gray.800" lineHeight="1.4" mb={2}>{title}</Text>
        <HStack spacing={3} mb={3} flexWrap="wrap" rowGap={1}>
          <HStack spacing={1.5}>
            <CalendarBlank size={13} color="#A0AEC0" />
            <Text fontSize="13px" color="gray.500">{task.year} {task.quarter}</Text>
          </HStack>
          <HStack spacing={1.5}>
            <Buildings size={13} color="#A0AEC0" />
            <Text fontSize="13px" color={task.division ? "gray.600" : "gray.500"} fontWeight={task.division ? "600" : "400"}>
              {task.division || COMPANY_WIDE}
            </Text>
          </HStack>
          <Tag size="sm" borderRadius="md" bg="#FFF8E1" px={2} py={0.5} gap={1.5}>
            <User size={12} color="#718096" />
            <Text fontSize="11px" fontWeight="600" color="gray.700" textTransform="uppercase">{task.assignee}</Text>
          </Tag>
        </HStack>
        <Box mt="auto" pt={2} maxW="60%" minW="120px">
          <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1}>{statusCfg.label}</Text>
          <Box h="10px" borderRadius="full" bg="gray.100" overflow="hidden">
            <Box h="100%" w={`${statusCfg.progress}%`} borderRadius="full" bg={statusCfg.color} />
          </Box>
        </Box>
      </CardBody>
    </Card>
  );
}

export default SupplierTaskCard;
